import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";

import { AppealState } from "../../types/appealState";

import styles from "./status-dropdown.module.scss";

interface StatusDropdownProps {
  selectedStatuses: AppealState[];
  onStatusChange: (statuses: AppealState[]) => void;
  availableStatuses: AppealState[];
}

export const StatusDropdown = ({
  selectedStatuses,
  onStatusChange,
  availableStatuses,
}: StatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleStatusSelect = (status: AppealState) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];

    onStatusChange(newStatuses);
  };

  const handleRemoveStatus = (
    status: AppealState,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    const newStatuses = selectedStatuses.filter((s) => s !== status);
    onStatusChange(newStatuses);
  };

  return (
    <div className={styles.statusDropdown} ref={dropdownRef}>
      <div
        role="button"
        tabIndex={0}
        className={styles.dropdownButton}
        onClick={handleToggleDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleDropdown();
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className={styles.buttonContent}>
          {selectedStatuses.length === 0 ? (
            <span className={styles.placeholder}>Appeal Status</span>
          ) : (
            <div className={styles.selectedPills}>
              {selectedStatuses.map((status) => (
                <span
                  key={status}
                  className={classNames(
                    styles.selectedPill,
                    styles[`pill${status.replace(/\s/g, "")}`],
                  )}
                >
                  {status}
                  <button
                    type="button"
                    className={styles.removePillButton}
                    onClick={(e) => handleRemoveStatus(status, e)}
                    aria-label={`Remove ${status} filter`}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={styles.chevronIcon}
          />
        </div>
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu} role="listbox">
          {availableStatuses.map((status) => {
            const isSelected = selectedStatuses.includes(status);

            return (
              <button
                key={status}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={classNames(
                  styles.dropdownOption,
                  styles[`pill${status.replace(/\s/g, "")}`],
                  {
                    [styles.selected]: isSelected,
                  },
                )}
                onClick={() => handleStatusSelect(status)}
              >
                {status}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
